import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useListing } from '../hooks/useListings';
import { useCollection } from '../hooks/useCollections';
import { useMyId } from '../hooks/useSession';
import type { Listing } from '../domain/listings';
import { formatPrice, listingTypeLabel } from '../utils/listings';
import { isMe } from '../utils/people';
import ConfirmDialog from '../components/ConfirmDialog';
import CloneDialog from '../components/CloneDialog';
import ListingLifecycleMenu from '../components/ListingLifecycleMenu';
import ListingStateBadge from '../components/ListingStateBadge';
import { ActionError, LoadingState, NotFoundState, StateForError } from '../components/PageStates';
import ShareListingDialog from '../components/ShareListingDialog';
import { isDomainError } from '../domain/errors';
import { useRepositories } from '../hooks/useRepositories';
import ListingBreadcrumbs from '../components/ListingBreadcrumbs';
import CommentSection from '../components/CommentSection';
import { PersonName } from '../components/PersonDialog';
import ReportDialog from '../components/ReportDialog';
import UserAvatar from '../components/UserAvatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft,
  CalendarClock,
  CopyPlus,
  Pencil,
  Share2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Flag,
  Clock,
  MapPin,
  MessageCircle,
  Package,
  User,
} from 'lucide-react';

export default function ListingDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const listing = useListing(id);
  // The listing as an action on this page left it; `gone` when the server no longer shows it.
  const [changed, setChanged] = useState<Listing | 'gone' | null>(null);

  if (listing.status === 'loading') return <LoadingState label="Loading listing" />;
  if (listing.status === 'error') {
    return <StateForError error={listing.error} onRetry={listing.reload} variant="listing" />;
  }
  if (changed === 'gone') return <NotFoundState variant="listing" />;
  const current = changed && changed.id === id ? changed : listing.data;
  // Keyed by listing so opening another one (a fresh copy) starts its view
  // over: an open dialog, photo position or draft never carries across, even
  // when the new listing arrives without a loading state in between.
  return (
    <ListingView
      key={current.id}
      listing={current}
      onChanged={setChanged}
      onGone={() => setChanged('gone')}
    />
  );
}

function formatShort(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Expiry can fall intraday (flash sales, events) — keep the time component.
function formatShortWithTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Detail({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-2xl bg-muted p-4 text-center">
      {icon}
      <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function ListingView({
  listing,
  onChanged,
  onGone,
}: {
  listing: Listing;
  onChanged: (listing: Listing) => void;
  onGone: () => void;
}) {
  const navigate = useNavigate();
  const photos = listing.photos;
  const [photoIndex, setPhotoIndex] = useState(0);
  const price = formatPrice(listing.price);
  const mine = isMe(listing.author, useMyId());
  const [reporting, setReporting] = useState(false);

  return (
    <div className="mx-auto max-w-4xl">
      <ListingBreadcrumbs listing={listing} />

      {photos.length > 0 && (
        <div className="mb-6">
          <div className="relative aspect-[16/9] overflow-hidden rounded-3xl bg-muted">
            {/* object-contain so portrait items aren't cropped — the muted
                backdrop letterboxes them within the hero. */}
            <img
              src={photos[photoIndex].url}
              alt={`${listing.title}, ${photoIndex + 1} of ${photos.length}`}
              className="size-full object-contain"
            />
            {photos.length > 1 && (
              <>
                <button
                  onClick={() => setPhotoIndex((i) => (i > 0 ? i - 1 : photos.length - 1))}
                  className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 text-foreground shadow-lg backdrop-blur-md transition-colors hover:bg-secondary"
                  aria-label="Previous photo"
                >
                  <ChevronLeft className="size-6" />
                </button>
                <button
                  onClick={() => setPhotoIndex((i) => (i < photos.length - 1 ? i + 1 : 0))}
                  className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-background/80 p-2 text-foreground shadow-lg backdrop-blur-md transition-colors hover:bg-secondary"
                  aria-label="Next photo"
                >
                  <ChevronRight className="size-6" />
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Editorial content shell — surface-container-lowest (paper-on-linen) */}
      <div className="rounded-[2rem] bg-popover p-6 md:p-8">
        <div className="mb-4 flex items-start justify-between gap-4">
          <Badge className="rounded-full border-transparent bg-wood/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-wood">
            {listingTypeLabel(listing.listingType)}
          </Badge>
          {price && (
            <span className="text-3xl font-extrabold tracking-tight text-primary">{price}</span>
          )}
        </div>

        <h1 className="mb-6 text-3xl font-extrabold leading-tight tracking-tight text-foreground md:text-4xl">
          {listing.title}
        </h1>

        <ListingStateBadge listing={listing} className="mb-6" />

        {mine && <OwnerActions listing={listing} onChanged={onChanged} onGone={onGone} />}

        {listing.isExpired && (
          <Alert className="mb-6 rounded-2xl border-none bg-muted">
            <Clock className="size-4" aria-hidden="true" />
            <AlertDescription>This listing has expired</AlertDescription>
          </Alert>
        )}

        {listing.description.trim() && (
          <section aria-label="Description" className="mb-8">
            <p className="whitespace-pre-wrap text-lg leading-relaxed text-muted-foreground">
              {listing.description}
            </p>
          </section>
        )}

        <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-4">
          {listing.location && (
            <Detail icon={<MapPin className="size-5 text-primary" />} label="Location">
              <span className="text-sm font-bold text-foreground">{listing.location}</span>
            </Detail>
          )}
          <Detail icon={<Package className="size-5 text-primary" />} label="Collection">
            <Link
              to={`/collections/${listing.collection.id}`}
              className="text-sm font-bold text-foreground transition-colors hover:text-primary"
            >
              {listing.collection.name}
            </Link>
          </Detail>
          <Detail icon={<User className="size-5 text-primary" />} label="Seller">
            <div className="flex items-center gap-2">
              <UserAvatar person={listing.author} className="size-6" textClassName="text-[10px]" />
              <PersonName
                person={listing.author}
                onBlocked={onGone}
                className="text-sm font-bold text-foreground"
              />
            </div>
          </Detail>
          <Detail icon={<Clock className="size-5 text-primary" />} label="Posted">
            <span className="text-sm font-bold text-foreground">
              {formatShort(listing.announcedAt ?? listing.createdAt)}
            </span>
          </Detail>
          {listing.expiresAt && (
            <Detail icon={<CalendarClock className="size-5 text-primary" />} label="Expires">
              <span className="text-sm font-bold text-foreground">
                {formatShortWithTime(listing.expiresAt)}
              </span>
            </Detail>
          )}
        </div>

        {!mine && <MessageAuthor listing={listing} />}
      </div>

      <ListingComments listing={listing} />

      <div className="mt-8 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 size-4" /> Back
        </Button>
        {!mine && (
          <Button
            variant="ghost"
            className="rounded-full text-muted-foreground"
            aria-label="Report this listing"
            onClick={() => setReporting(true)}
          >
            <Flag className="mr-1 size-4" aria-hidden="true" /> Report
          </Button>
        )}
        <Button variant="link" asChild className="p-0 text-primary hover:text-primary/80">
          <Link to={`/collections/${listing.collection.id}`}>View collection</Link>
        </Button>
      </div>
      {reporting && (
        <ReportDialog
          target={{ kind: 'listing', id: listing.id }}
          open
          onOpenChange={(open) => !open && setReporting(false)}
        />
      )}
    </div>
  );
}

/**
 * Message the author about this listing (018 E22): our one direct
 * conversation, now about this listing. A refusal shows the server's reason.
 */
function MessageAuthor({ listing }: { listing: Listing }) {
  const { conversations } = useRepositories();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const c = await conversations.openDirect(listing.author.id, listing.id);
      navigate(`/conversations/${c.id}`);
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Button
        onClick={open}
        disabled={busy}
        className="h-auto w-full rounded-full py-5 text-base font-bold shadow-lg shadow-primary/20"
      >
        <MessageCircle className="mr-2 size-5" /> Message {listing.author.firstName}
      </Button>
      {error !== null && <ActionError error={error} />}
    </div>
  );
}

/**
 * The comment section, shown only while the listing's collection has comments
 * on (R22) — that's the collection's setting, so it's read from the
 * collection rather than guessed from the listing.
 */
function ListingComments({ listing }: { listing: Listing }) {
  const collection = useCollection(listing.collection.id);

  if (collection.status === 'loading') return null;
  if (collection.status === 'error') {
    return (
      <p role="alert" className="mt-8 rounded-3xl bg-card p-6 text-muted-foreground">
        Couldn't load comments.{' '}
        <button className="font-semibold text-primary hover:underline" onClick={collection.reload}>
          Try again
        </button>
      </p>
    );
  }
  if (!collection.data.commentsEnabled) return null;
  return (
    <div className="mt-8">
      <CommentSection listingId={listing.id} />
    </div>
  );
}

/**
 * The author's actions (016): the lifecycle, Edit, copy to another
 * collection, Share and Delete. Each shows as done only once the server
 * agrees; a listing that's gone meanwhile turns into the no-access page.
 */
function OwnerActions({
  listing,
  onChanged,
  onGone,
}: {
  listing: Listing;
  onChanged: (listing: Listing) => void;
  onGone: () => void;
}) {
  const navigate = useNavigate();
  const { listings } = useRepositories();
  const [dialog, setDialog] = useState<'clone' | 'share' | 'delete' | null>(null);
  const [error, setError] = useState<unknown>(null);

  const remove = async () => {
    setError(null);
    try {
      await listings.delete(listing.id);
      navigate(`/collections/${listing.collection.id}`);
    } catch (e) {
      if (isDomainError(e, 'notFound')) onGone();
      else setError(e);
    }
  };

  const close = (open: boolean) => !open && setDialog(null);

  return (
    <div className="mb-8 space-y-4 rounded-2xl bg-muted p-4">
      <ListingLifecycleMenu listing={listing} onChanged={onChanged} onGone={onGone} />
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" size="sm" className="rounded-full" asChild>
          <Link to={`/listings/${listing.id}/edit`}>
            <Pencil />
            Edit
          </Link>
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="rounded-full"
          onClick={() => setDialog('clone')}
        >
          <CopyPlus />
          Copy to another collection
        </Button>
        <Button
          variant="secondary"
          size="sm"
          className="rounded-full"
          onClick={() => setDialog('share')}
        >
          <Share2 />
          Share
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full text-destructive"
          onClick={() => setDialog('delete')}
        >
          <Trash2 />
          Delete
        </Button>
      </div>
      {error !== null && <ActionError error={error} />}

      <CloneDialog
        listing={listing}
        open={dialog === 'clone'}
        onOpenChange={close}
        onCloned={(copy) => navigate(`/listings/${copy.id}`)}
      />
      <ShareListingDialog listing={listing} open={dialog === 'share'} onOpenChange={close} />
      <ConfirmDialog
        open={dialog === 'delete'}
        onOpenChange={close}
        title={`Delete ${listing.title}?`}
        description={
          <p>
            It and its comments are deleted for everyone. Copies you made in other collections stay.
            This can't be undone.
          </p>
        }
        confirmLabel="Delete"
        onConfirm={remove}
      />
    </div>
  );
}
